import React, {useEffect, useState} from 'react';
import authService from '../services/authService';
import {clearSession, setSession} from '../services/session';
import {resetCsrfToken} from '../services/apiRequest';

export const AuthContext = React.createContext();

const AuthContextProvider = ({children}) => {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(false);
  // Session payload from the backend (user, role, expires_at). The JWT itself is
  // HttpOnly and never readable here.
  const [session, setSessionState] = useState({user: null, role: null, expiresAt: null});
  // False until the initial GET /auth/me resolves — PrivateRoute waits for it,
  // so a logged-in user opening an email link (fresh page load) is not bounced
  // to /login before the cookie session has been checked.
  const [booted, setBooted] = useState(false);

  const applySession = next => {
    setSession(next);
    setSessionState(next && next.user ? next : {user: null, role: null, expiresAt: null});
  };

  const boot = async () => {
    const me = await authService.me();
    if (me && me.user) {
      applySession(me);
    } else {
      applySession(null);
    }
    setBooted(true);
  };

  useEffect(() => {
    boot();
    const onSessionExpired = () => {
      clearSession();
      resetCsrfToken();
      setSessionState({user: null, role: null, expiresAt: null});
    };
    window.addEventListener('wolte:session-expired', onSessionExpired);
    return () => window.removeEventListener('wolte:session-expired', onSessionExpired);
  }, []);

  const logIn = async payload => {
    setError(false);
    setPending(true);
    const data = await authService.logIn(payload);
    setPending(false);
    if (data && data.user) {
      applySession(data);
      return data;
    }
    setError(true);
    return data || {type: 'Error', status: 401};
  }

  const logOut = async () => {
    setError(false);
    setPending(false);
    try {
      await authService.logOut();
    } catch (e) {
      // Cookies are cleared server-side even on failures; proceed with local cleanup.
    }
    // Logout deletes the XSRF-TOKEN cookie server-side — the held CSRF token is
    // invalid for the next (anonymous) login, so re-bootstrap before it.
    resetCsrfToken();
    clearSession();
    setSessionState({user: null, role: null, expiresAt: null});
  }

  return (
    <AuthContext.Provider
      value={{booted, session, user: session.user, role: session.role, expiresAt: session.expiresAt, pending, error, logIn, logOut}}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContextProvider;
